import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp";

const initialState = {
	// Courses list
	coursesList: [],
	course: null,
	isLoading: false,
	error: null,

	// Difficulties
	difficulties: [],
	difficultiesLoading: false,

	// Pagination
	pagination: {
		currentPage: 1,
		pageSize: 10,
		total: 0,
	},

	// Stats
	stats: {
		totalCourses: 0,
		activeCourses: 0,
		draftCourses: 0,
		inactiveCourses: 0,
	},
};

// Fetch All Courses
export const fetchCoursesList = createAsyncThunk("lmsCourses/fetchCoursesList", async ({ page, limit, filters } = {}, { rejectWithValue }) => {
	try {
		const params = new URLSearchParams({
			page: page ?? 1,
			limit: limit ?? 10,
			category_id: filters?.category_id ?? "",
			subcategory_id: filters?.subcategory_id ?? "",
			difficulty_id: filters?.difficulty_id ?? "",
			status: filters?.status ?? "",
			search: filters?.search ?? "",
		});

		const response = await axiosHttp.get(`lms/courses?${params}`);
		return response.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch Course by ID
export const fetchCourseById = createAsyncThunk("lmsCourses/fetchCourseById", async ({ courseId }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get(`lms/courses/${courseId}`);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Create Course
export const createCourse = createAsyncThunk("lmsCourses/createCourse", async (courseData, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post("lms/courses", courseData);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Update Course
export const updateCourse = createAsyncThunk("lmsCourses/updateCourse", async ({ courseId, ...updateData }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.put(`lms/courses/${courseId}`, updateData);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Delete Course
export const deleteCourse = createAsyncThunk("lmsCourses/deleteCourse", async ({ courseId }, { rejectWithValue }) => {
	try {
		await axiosHttp.delete(`lms/courses/${courseId}`);
		return courseId;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Fetch Difficulties
export const fetchDifficulties = createAsyncThunk("lmsCourses/fetchDifficulties", async (_, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.get("lms/courses/difficulties");
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Create Course Section
export const createCourseSection = createAsyncThunk("lmsCourses/createCourseSection", async ({ courseId, sectionData }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.post(`lms/courses/${courseId}/sections`, sectionData);
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Update Course Section
export const updateCourseSection = createAsyncThunk(
	"lmsCourses/updateCourseSection",
	async ({ courseId, sectionId, ...updateData }, { rejectWithValue }) => {
		try {
			const response = await axiosHttp.put(`lms/courses/${courseId}/sections/${sectionId}`, updateData);
			return response.data.data;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message);
		}
	},
);

// Delete Course Section
export const deleteCourseSection = createAsyncThunk("lmsCourses/deleteCourseSection", async ({ courseId, sectionId }, { rejectWithValue }) => {
	try {
		await axiosHttp.delete(`lms/courses/${courseId}/sections/${sectionId}`);
		return sectionId;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Reorder Sections
export const reorderSections = createAsyncThunk("lmsCourses/reorderSections", async ({ courseId, sections }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.put(`lms/courses/${courseId}/sections/reorder`, { sections });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

// Publish/Unpublish Course
export const publishCourse = createAsyncThunk("lmsCourses/publishCourse", async ({ courseId, status }, { rejectWithValue }) => {
	try {
		const response = await axiosHttp.patch(`lms/courses/${courseId}/publish`, { status });
		return response.data.data;
	} catch (error) {
		return rejectWithValue(error.response?.data?.message || error.message);
	}
});

const coursesSlice = createSlice({
	name: "lmsCourses",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		clearCourse: (state) => {
			state.course = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch Courses List
			.addCase(fetchCoursesList.pending, (state) => {
				state.isLoading = true;
				state.error = null;
			})
			.addCase(fetchCoursesList.fulfilled, (state, action) => {
				state.isLoading = false;
				if (action.payload.data && Array.isArray(action.payload.data)) {
					state.coursesList = action.payload.data;
					state.pagination = {
						currentPage: action.payload.page || 1,
						pageSize: action.payload.limit || 10,
						total: action.payload.total || 0,
					};
					if (action.payload.stats) {
						state.stats = action.payload.stats;
					}
				} else if (Array.isArray(action.payload)) {
					state.coursesList = action.payload;
					state.pagination.total = action.payload.length;
				}
			})
			.addCase(fetchCoursesList.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Course by ID
			.addCase(fetchCourseById.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(fetchCourseById.fulfilled, (state, action) => {
				state.isLoading = false;
				state.course = action.payload;
			})
			.addCase(fetchCourseById.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Create Course
			.addCase(createCourse.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(createCourse.fulfilled, (state, action) => {
				state.isLoading = false;
				state.coursesList.unshift(action.payload);
			})
			.addCase(createCourse.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Update Course
			.addCase(updateCourse.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(updateCourse.fulfilled, (state, action) => {
				state.isLoading = false;
				const index = state.coursesList.findIndex((c) => c.id === action.payload.id);
				if (index !== -1) {
					state.coursesList[index] = action.payload;
				}
				if (state.course && state.course.id === action.payload.id) {
					state.course = action.payload;
				}
			})
			.addCase(updateCourse.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Delete Course
			.addCase(deleteCourse.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(deleteCourse.fulfilled, (state, action) => {
				state.isLoading = false;
				state.coursesList = state.coursesList.filter((c) => c.id !== action.payload);
			})
			.addCase(deleteCourse.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			})

			// Fetch Difficulties
			.addCase(fetchDifficulties.pending, (state) => {
				state.difficultiesLoading = true;
			})
			.addCase(fetchDifficulties.fulfilled, (state, action) => {
				state.difficultiesLoading = false;
				state.difficulties = action.payload;
			})
			.addCase(fetchDifficulties.rejected, (state, action) => {
				state.difficultiesLoading = false;
			})

			// Publish Course
			.addCase(publishCourse.pending, (state) => {
				state.isLoading = true;
			})
			.addCase(publishCourse.fulfilled, (state, action) => {
				state.isLoading = false;
				const index = state.coursesList.findIndex((c) => c.id === action.payload.id);
				if (index !== -1) {
					state.coursesList[index] = action.payload;
				}
				if (state.course && state.course.id === action.payload.id) {
					state.course = action.payload;
				}
			})
			.addCase(publishCourse.rejected, (state, action) => {
				state.isLoading = false;
				state.error = action.payload;
			});
	},
});

export const { clearError, clearCourse } = coursesSlice.actions;
export default coursesSlice.reducer;
